import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useCartItemReference } from './cart-item-reference/use-cart-item-reference.hook';
import { useCheckoutCartActions } from './checkout-cart/checkout-cart-actions.hook';
import { useCreateEmptyCart } from './cart-mutation.hook';
import { forceCartItemReferenceSync, withInternalUpdate } from '../stores/cart-item-reference.store';
import {
    type CartItemReference,
    type CartItemReferenceLogic,
    type CartItemLocalInterface,
    CartItemTypeEnum,
} from '../models';
import type { CartItemInterface, ConfigurableCartItem } from '../types/front.graphql';
import isEmpty from 'lodash/isEmpty';
import { cartStoreAPI } from '../stores/cart-store.public-api';
import { updateCartItemQuantityAndPrices } from '../helpers/cart-item.helpers';
import { type ProductModel } from '@kidslabs/module-catalog';

/**
 * Product input for buy-now action.
 * The caller is responsible for creating the cart item (e.g. via generateWebCartItem)
 * and attaching gift logics (via GiftsSelectorWidget).
 */
export interface BuyNowProductInput {
    /** Cart item to add (created from generateWebCartItem or equivalent) */
    item: CartItemInterface;
    /** Quantity to add (defaults to 1) */
    quantity?: number;
    /** Gift / promotion logics attached to this product */
    cartItemReferenceLogics?: CartItemReferenceLogic[];
    /**
     * Parent product for configurable products.
     * Pass the ConfigurableProduct when adding a specific variant.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parentProduct?: any;
}

export interface UseBuyNowAction {
    /**
     * Buy now with a single product.
     * Adds product to cart → unselects all → selects only this → force sync → navigate to checkout.
     */
    handleBuyNowSingle: (input: BuyNowProductInput) => Promise<void>;

    /**
     * Buy now with multiple products (cross-sell combo).
     * Adds all products to cart → unselects all → selects only these → force sync → navigate to checkout.
     */
    handleBuyNowMultiple: (inputs: BuyNowProductInput[]) => Promise<void>;

    /** Whether a buy-now action is currently in progress */
    loading: boolean;

    /** Error message from the last buy-now action */
    errorMessage: string | undefined;
}

/**
 * Hook for handling buy-now and cross-sell buy-now actions.
 *
 * Complete flow:
 * 1. Adding product(s) to draft cart
 * 2. Setting cart item reference selections (only the bought products are selected)
 * 3. Force syncing cart to server (bypassing debounce)
 * 4. Navigating to checkout page
 *
 * ARCHITECTURE:
 * - Business logic only (no UI) — follows Container/View separation
 * - Gift selection is handled by the caller (e.g. flash-sale GiftsSelectorWidget)
 * - Force sync ensures server has latest cart state before entering checkout
 */
export const useBuyNowAction = (): UseBuyNowAction => {
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const { initCart: initCheckoutCart } = useCheckoutCartActions();
    const { createEmptyCart } = useCreateEmptyCart();

    const {
        addCartItemReference,
        updateCartItemReference,
        initCartItemReference,
        isPromotionReference,
        getReferenceById,
        getCartItemReferencesByProductId,
        unselectedAllCartItemReferenceSelected,
        setCartItemReferenceSelected,
    } = useCartItemReference();

    /** Helper: Extract the correct product ID from a cart item */
    const getProductId = useCallback((item: CartItemInterface, parentProduct?: ProductModel): number => {
        const typeName = (item as { __typename?: string }).__typename;
        const isConfigurable =
            typeName === CartItemTypeEnum.CONFIGURABLE || typeName === CartItemTypeEnum.CONFIGURABLE_LOCAL;

        if (parentProduct && isConfigurable) {
            const configurableItem = item as ConfigurableCartItem;
            return configurableItem.configured_variant?.id || item?.product?.id || 0;
        }
        return item?.product?.id || 0;
    }, []);

    /**
     * Initialize cart ID — creates empty cart if needed
     */
    const initializeCartId = useCallback(async (): Promise<string> => {
        let cartId = cartStoreAPI.state.getCartId();
        if (!cartId || cartId === '') {
            const emptyCartIdResponse = await createEmptyCart();
            cartId = emptyCartIdResponse?.data?.createEmptyCart ?? '';
            if (isEmpty(cartId)) {
                throw new Error('Failed to create an empty cart.');
            }
            cartStoreAPI.actions.setCartId(cartId);
            initCheckoutCart(cartId);
        }
        return cartId ?? '';
    }, [createEmptyCart, initCheckoutCart]);

    /**
     * Add a single product to CartItemReference store and return the reference.
     */
    const findExistingCartItemReference = useCallback(
        ({
            productId,
            cartItemReferenceLogics,
            hasNewLogics,
        }: {
            productId: number;
            cartItemReferenceLogics?: CartItemReferenceLogic[];
            hasNewLogics: boolean;
        }): CartItemReference | undefined => {
            if (hasNewLogics) {
                return undefined;
            }

            const referenceIdFromLogics = cartItemReferenceLogics?.[0]?.cartItemReferenceId;
            if (referenceIdFromLogics) {
                return getReferenceById(referenceIdFromLogics);
            }

            const refs = getCartItemReferencesByProductId(productId);
            return refs.find(ref => !ref.logics || ref.logics.length === 0);
        },
        [getCartItemReferencesByProductId, getReferenceById]
    );

    const createCartItemReference = useCallback(
        ({
            item,
            quantity,
            productId,
            parentProduct,
            logics = [],
        }: {
            item: CartItemInterface;
            quantity: number;
            productId: number;
            parentProduct?: ProductModel;
            logics?: CartItemReferenceLogic[];
        }): CartItemReference => {
            const newRef = initCartItemReference({
                productId,
                qty: quantity,
                logics,
                cartItemId: item.id ?? '',
                isSelected: false,
                createdAt: new Date().toISOString(),
                parentProduct,
                addToStore: false,
            });

            const typeName = (item as { __typename?: string }).__typename;
            const isConfigurable =
                typeName === CartItemTypeEnum.CONFIGURABLE || typeName === CartItemTypeEnum.CONFIGURABLE_LOCAL;

            // Derive variant SKU for configurable products
            if (parentProduct && isConfigurable) {
                const configurableItem = item as ConfigurableCartItem;
                newRef.sku = configurableItem.configured_variant?.sku ?? item?.product?.sku ?? undefined;
            } else {
                newRef.sku = item?.product?.sku ?? undefined;
            }

            newRef.cartItem = item as unknown as CartItemLocalInterface;
            addCartItemReference(newRef);
            return newRef;
        },
        [addCartItemReference, initCartItemReference]
    );

    const resolveCartItemReference = useCallback(
        ({
            item,
            quantity,
            productId,
            parentProduct,
            cartItemReferenceLogics,
            existingCartItemReference,
            hasNewLogics,
        }: {
            item: CartItemInterface;
            quantity: number;
            productId: number;
            parentProduct?: ProductModel;
            cartItemReferenceLogics?: CartItemReferenceLogic[];
            existingCartItemReference?: CartItemReference;
            hasNewLogics: boolean;
        }): CartItemReference | undefined => {
            if (hasNewLogics) {
                return createCartItemReference({
                    item,
                    quantity,
                    productId,
                    parentProduct,
                    logics: cartItemReferenceLogics,
                });
            }

            if (!existingCartItemReference) {
                return createCartItemReference({
                    item,
                    quantity,
                    productId,
                    parentProduct,
                    logics: [],
                });
            }

            if (isPromotionReference(existingCartItemReference)) {
                return undefined;
            }

            return existingCartItemReference;
        },
        [createCartItemReference, isPromotionReference]
    );

    const buildCartItemToUpdate = useCallback(
        ({
            item,
            quantity,
            resultReference,
            isNewRef,
        }: {
            item: CartItemInterface;
            quantity: number;
            resultReference: CartItemReference;
            isNewRef: boolean;
        }): CartItemLocalInterface => {
            const existingCartItem = resultReference.cartItem;

            if (existingCartItem && !isNewRef) {
                const newQty = resultReference.qty + quantity;
                return updateCartItemQuantityAndPrices(existingCartItem, newQty);
            }

            const updatedCartItem = updateCartItemQuantityAndPrices(
                item as unknown as CartItemLocalInterface,
                quantity
            ) as CartItemLocalInterface;

            updatedCartItem.cartItemReferenceId = resultReference.cartItemReferenceId;
            return updatedCartItem;
        },
        []
    );

    const addProductToCartItemReference = useCallback(
        (input: BuyNowProductInput): CartItemReference | undefined => {
            const { item, quantity = 1, cartItemReferenceLogics, parentProduct } = input;
            const productId = getProductId(item, parentProduct);
            const hasNewLogics = !!cartItemReferenceLogics?.length;

            const existingCartItemReference = findExistingCartItemReference({
                productId,
                cartItemReferenceLogics,
                hasNewLogics,
            });

            const resultReference = resolveCartItemReference({
                item,
                quantity,
                productId,
                parentProduct,
                cartItemReferenceLogics,
                existingCartItemReference,
                hasNewLogics,
            });

            if (!resultReference) {
                return undefined;
            }

            const isNewRef = hasNewLogics || !existingCartItemReference;
            const cartItemToUpdate = buildCartItemToUpdate({
                item,
                quantity,
                resultReference,
                isNewRef,
            });

            let newReferenceQty = quantity;
            if (!isNewRef) {
                newReferenceQty = resultReference.qty + quantity;
            }

            const updatedReference: CartItemReference = {
                ...resultReference,
                qty: newReferenceQty,
                cartItem: cartItemToUpdate,
            };

            updateCartItemReference(updatedReference);
            return updatedReference;
        },
        [
            buildCartItemToUpdate,
            findExistingCartItemReference,
            getProductId,
            resolveCartItemReference,
            updateCartItemReference,
        ]
    );

    /**
     * Add products to cart and return their cart item references
     */
    const addProductsToCart = useCallback(
        (inputs: BuyNowProductInput[]): CartItemReference[] => {
            const addedReferences: CartItemReference[] = [];

            for (const input of inputs) {
                const cartItemReference = addProductToCartItemReference(input);

                if (cartItemReference) {
                    addedReferences.push(cartItemReference);
                }
            }

            return addedReferences;
        },
        [addProductToCartItemReference]
    );

    /**
     * Set selections: unselect all, then select only the given references
     */
    const setSelectionsForCheckout = useCallback(
        (referenceIds: string[]) => {
            unselectedAllCartItemReferenceSelected();

            for (const referenceId of referenceIds) {
                setCartItemReferenceSelected(referenceId, true);
            }

            console.log('[useBuyNowAction] Selections set for checkout', {
                selectedReferenceIds: referenceIds,
            });
        },
        [setCartItemReferenceSelected, unselectedAllCartItemReferenceSelected]
    );

    /**
     * Core buy-now flow for multiple products
     */
    const handleBuyNowMultiple = useCallback(
        async (inputs: BuyNowProductInput[]): Promise<void> => {
            if (inputs.length === 0) {
                console.warn('[useBuyNowAction] No products to buy');
                return;
            }

            setLoading(true);
            setErrorMessage(undefined);

            try {
                // Step 1: Initialize cart
                await initializeCartId();

                // Suppress auto-sync during buy-now mutations to prevent race conditions.
                // Without this, debounce timers fire during addProductsToCart and sync
                // stale selection state where previously-selected items are still checked.
                const addedReferences = await withInternalUpdate(() => {
                    // Step 2: Add all products to cart (CartItemReference store)
                    const refs = addProductsToCart(inputs);

                    if (refs.length === 0) {
                        throw new Error('Failed to add products to cart');
                    }

                    // Step 3: Set selections — only the newly added products
                    const referenceIds = refs.map(ref => ref.cartItemReferenceId);
                    setSelectionsForCheckout(referenceIds);

                    return refs;
                });

                if (addedReferences.length === 0) {
                    throw new Error('Failed to add products to cart');
                }

                // Step 4: Force sync to server (auto-sync re-enabled, bypass debounce)
                await forceCartItemReferenceSync();

                // Step 5: Navigate to checkout
                router.push('/payment/checkout');
            } catch (error) {
                const message = error instanceof Error ? error.message : 'An error occurred during buy now';
                console.error('[useBuyNowAction] Buy now failed:', message);
                setErrorMessage(message);
            } finally {
                setLoading(false);
            }
        },
        [addProductsToCart, initializeCartId, router, setSelectionsForCheckout]
    );

    /**
     * Single product buy-now (convenience wrapper)
     */
    const handleBuyNowSingle = useCallback(
        async (input: BuyNowProductInput): Promise<void> => {
            return handleBuyNowMultiple([input]);
        },
        [handleBuyNowMultiple]
    );

    return {
        handleBuyNowSingle,
        handleBuyNowMultiple,
        loading: loading,
        errorMessage,
    };
};
